import allocate from "../util/allocate";
import Vector from "../util/vector";
import Simplex from "../util/noise";


const ATTRACTION_FORCE = 0.01;

/** Force with which the mouse pointer repells points */
const MOUSE_FORCE = 0.02;

/** Preferred distance for mouse repulsion */
const MOUSE_REPULSION_DISTANCE = 100;

/** controls the loss of impulse for our points. lower friction leads to longer, more springy movement */
const FRICTION = 0.03;

/**
 * Number physically simulated circle points forming a regular inscribed polygon. Must be a multiple of 3.
 *
 * Each has a repulsive spring connection to its neighbor points and both a repulsive and attractive connection
 * to a invisible center point
 *
 * Also we are connecting the points across the circle in a triangle formation where every dot is connected to the
 * dots a third of the circle to the left and to the right of it. (hence the multiple of 3 requirement)
 *
 */
export const CIRCLE_RESOLUTION = 9;

/** How much mass does the invisible center point have? ( circle points have a mass of 1) */
const CENTER_MASS = 1;

/** how large is the sine-movement of the ideal center target points compared to the circle size? */
const MOVEMENT_FACTOR = 0.07;

/** distance of the control points in relation to the radius size */
const CONTROL_SCALE = 0.25;

/** speed multiplier for speed table below */
const SPEED_FACTOR  = 0.004;

const NOISE_SCALE = 0.5;

/** Speeds picked at random for the sine-based center target position */
const SPEEDS = [
        SPEED_FACTOR,
    3 * SPEED_FACTOR,
    5 * SPEED_FACTOR,
    7 * SPEED_FACTOR,
    11 * SPEED_FACTOR,
    13 * SPEED_FACTOR,
    17 * SPEED_FACTOR,
];

////////////////////////////////////////////////// INTERNAL CONSTANTS //////////////////////////////////////////////////

const TAU = Math.PI * 2;

// Array elements per simulation point: x,y, dx,dy, mass
const ELEMENTS = 5;

/** <ELEMENTS> float values per circle point */
const CIRCLE_POINT_LEN = CIRCLE_RESOLUTION * ELEMENTS;
/** Array offset for the center target point which is the point the center follows */
const CENTER_TARGET_OFFSET = CIRCLE_POINT_LEN;
/** current mouse pos offset */
const MOUSE_POS_OFFSET = CIRCLE_POINT_LEN + ELEMENTS;

const AVERAGE_POS_OFFSET = CIRCLE_POINT_LEN + 2 * ELEMENTS;

// .. plus the invisible center point plus center target plus mouse pos
const DATA_LEN = CIRCLE_POINT_LEN + 3 * ELEMENTS;

const sqrt = Math.sqrt;

const simplex = new Simplex(Math.random);


function getConfigSpeedOrRandom(config, idx)
{
    const v = config.speeds && config.speeds[idx];
    if (typeof v === "number")
    {
        return v;
    }

    return SPEEDS[(Math.random() * SPEEDS.length)|0];
}

/**
 * Returns the edge size of a n-sided regular inscribed polygon inside a circle of the given radius.
 *
 * @param n             number of sides
 * @param radius        radius
 * @returns {number}
 */
function getEdgeLength(n, radius)
{
    return 2 * radius * Math.sin(TAU / n / 2);
}

let vControl1 = new Vector(0,0);
let vControl2 = new Vector(0,0);

class Circle
{
    constructor(ctx, x, y, size, config)
    {
        this.ctx = ctx;
        this.x = x;
        this.y = y;
        this.size = size;
        this.color = config.color;
        this.lineWidth = config.lineWidth;

        this.noiseX = 0;
        this.noiseY = Math.random() * 100;

        this.controlPointDistance = CONTROL_SCALE * size/2;

        const angleStep = TAU / CIRCLE_RESOLUTION;

        const data = allocate(DATA_LEN);
        this.data = data;
        const radius = size * 0.5;

        for (let i = 0, a = 0; i < CIRCLE_POINT_LEN ; i += ELEMENTS, a += angleStep)
        {
            // init all circle points with position in <size> distance and zero velocity
            data[i    ] = this.x + Math.cos(a) * radius;
            data[i + 1] = this.y + Math.sin(a) * radius;
            data[i + 2] = 0;
            data[i + 3] = 0;
            data[i + 4] = 1;
        }
    }


    render(stats)
    {
        const { ctx , data, minDistanceIndex} = this;

        ctx.lineWidth = this.lineWidth;
        ctx.strokeStyle = this.color;

        let x0 = data[0];
        let y0 = data[1];

        ctx.save();
        ctx.beginPath();
        ctx.globalAlpha = 0.6;
        ctx.moveTo(x0, y0);

        for (let i = ELEMENTS; i < CIRCLE_POINT_LEN ; i += ELEMENTS)
        {
            let x1 = data[i];
            let y1 = data[i + 1];

            this.drawBezier( x0, y0, x1, y1, stats);
            x0 = x1;
            y0 = y1;
        }

        this.drawBezier(x0, y0, data[0], data[1], stats);
        ctx.stroke();
        ctx.restore();

        if (minDistanceIndex > 0)
        {
            let x = data[minDistanceIndex    ];
            let y = data[minDistanceIndex + 1];
            
            ctx.strokeStyle = "rgba(255,255,255,0.66)";
            ctx.beginPath();
            ctx.arc(x, y, 5, 0, TAU, false);
            ctx.stroke();
        }

        if (process.env.NODE_ENV !== "production")
        {
            if (stats.cpLine)
            {
                const { data } = this;

                const centerTargetX = data[CENTER_TARGET_OFFSET    ];
                const centerTargetY = data[CENTER_TARGET_OFFSET + 1];
                const averageX = data[AVERAGE_POS_OFFSET    ];
                const averageY = data[AVERAGE_POS_OFFSET + 1];

                ctx.fillStyle = "#f00";
                ctx.fillRect(centerTargetX, centerTargetY, 2, 2);

                ctx.fillStyle = "#0f0";
                ctx.fillRect( averageX, averageY, 2, 2);
            }

            ctx.fillStyle = "#00f";
            for (let i=0 ; i < stats.nPos; i+=2)
            {
                let x = stats.noise[i];
                let y = stats.noise[i + 1];
                ctx.fillRect( x, y, 4, 4);

                stats.aabb.extend(x,y);
            }
        }
    }

    drawBezier(x1, y1, x2, y2, stats)
    {
        const centerX = this.x;//data[AVERAGE_POS_OFFSET    ];
        const centerY = this.y;//data[AVERAGE_POS_OFFSET + 1];

        vControl1.x = centerX - x1;
        vControl1.y = centerY - y1;

        vControl2.x = centerX - x2;
        vControl2.y = centerY - y2;

        vControl1.rotateClockwise().norm(this.controlPointDistance).add(x1,y1);
        vControl2.rotateCounterClockwise().norm(this.controlPointDistance).add(x2,y2);

        const { ctx } = this;

        if (process.env.NODE_ENV !== "production")
        {
            if (stats.cpLine)
            {
                // if we have a buffer we collect the debug lines between circle points and control points
                let pos = stats.cpPos;
                stats.cpLine[pos++] = x1;
                stats.cpLine[pos++] = y1;
                stats.cpLine[pos++] = vControl1.x;
                stats.cpLine[pos++] = vControl1.y;
                stats.cpLine[pos++] = vControl2.x;
                stats.cpLine[pos++] = vControl2.y;
                stats.cpLine[pos++] = x2;
                stats.cpLine[pos++] = y2;
                stats.cpPos = pos;

            }
        }

        // .. and extend the current AABB by the control points
        stats.aabb.extend(vControl1.x, vControl1.y);
        stats.aabb.extend(vControl2.x, vControl2.y);

        ctx.bezierCurveTo(vControl1.x, vControl1.y, vControl2.x, vControl2.y, x2, y2);
    }

    update(stats)
    {

        const { data, size} = this;
        const { aabb } = stats;

        const centerTargetX = data[CENTER_TARGET_OFFSET    ];
        const centerTargetY = data[CENTER_TARGET_OFFSET + 1];

        //aabb.extend(centerTargetX,centerTargetY);


        // find average center
        // let averageCenterX = data[0];
        // let averageCenterY = data[1];
        // for (let i = ELEMENTS; i < CIRCLE_POINT_LEN ; i += ELEMENTS)
        // {
        //     averageCenterX += data[i];
        //     averageCenterY += data[i + 1];
        // }
        //
        // averageCenterX /= CIRCLE_RESOLUTION;
        // averageCenterY /= CIRCLE_RESOLUTION;
        //
        // this.data[AVERAGE_POS_OFFSET    ] = averageCenterX;
        // this.data[AVERAGE_POS_OFFSET + 1] = averageCenterY;

        const angleStep = TAU / CIRCLE_RESOLUTION;


        const mouseX = data[MOUSE_POS_OFFSET    ];
        const mouseY = data[MOUSE_POS_OFFSET + 1];

        const radius = size/2;


        for (let i = 0, a = 0; i < CIRCLE_POINT_LEN ; i += ELEMENTS, a += angleStep)
        {
            const x0 = data[i    ];
            const y0 = data[i + 1];

            const mass = data[i + 4];

            const noiseX = this.noiseX + Math.cos(a) * NOISE_SCALE;
            const noiseY = this.noiseY + Math.sin(a) * NOISE_SCALE;

            // noise scaling
            const noise = 0.8 + (simplex.noise2D(noiseX, noiseY) + 1) * 0.04;

            const targetX = this.x + Math.cos(a) * radius * noise;
            const targetY = this.y + Math.sin(a) * radius * noise;

            stats.noise[stats.nPos++] = targetX;
            stats.noise[stats.nPos++] = targetY;
            

            let x = targetX - x0;
            let y = targetY - y0;

            const targetDistance = sqrt( x * x + y * y);
            if (targetDistance > 0)
            {
                const f = ATTRACTION_FORCE * sqrt(targetDistance);

                // scale target vector to distance beyond limit
                const sx = x * f / targetDistance;
                const sy = y * f / targetDistance;

                data[i + 2] += sx / mass;
                data[i + 3] += sy / mass;
            }


            x = mouseX - x0;
            y = mouseY - y0;

            const mouseDistance = sqrt( x * x + y * y);

            if (mouseDistance < MOUSE_REPULSION_DISTANCE && mouseDistance > 0)
            {
                // scale force proportional to the current distance
                const f = MOUSE_FORCE * sqrt(MOUSE_REPULSION_DISTANCE - mouseDistance);

                // scale target vector to force
                const sx = x * f / mouseDistance;
                const sy = y * f / mouseDistance;

                data[i + 2] -= sx / mass;
                data[i + 3] -= sy / mass;
            }

            this.noiseX += 0.001;
        }


        // apply deltas for all circle points
        for (let i = 0; i < CIRCLE_POINT_LEN ; i += ELEMENTS)
        {
            let x = data[i    ];
            let y = data[i + 1];

            x += data[i + 2];
            y += data[i + 3];

            data[i    ] = x;
            data[i + 1] = y;
            data[i + 2] *= (1 - FRICTION);
            data[i + 3] *= (1 - FRICTION);

            aabb.extend(x,y);
        }

    }

    repulse(i, off1, distance, force)
    {
        const { data } = this;

        const x0 = data[i    ];
        const y0 = data[i + 1];

        const x = data[off1    ] - x0;
        const y = data[off1 + 1] - y0;

        const d = sqrt( x * x + y * y);

        const mass0 = data[i + 4];
        const mass1 = data[off1 + 4];

        if (d < distance && d > 0)
        {
            // scale force proportional to the current distance
            const f = force * sqrt(d);

            // scale target vector to force
            const sx = x * f / d;
            const sy = y * f / d;

            data[i + 2] -= sx / mass0;
            data[i + 3] -= sy / mass0;

            data[off1 + 2] += sx / mass1;
            data[off1 + 3] += sy / mass1;

        }
    }


    updateMouse(x,y)
    {
        const { data, isDragged } = this;

        data[MOUSE_POS_OFFSET] = x;
        data[MOUSE_POS_OFFSET + 1] = y;
    }
}

export default Circle



// WEBPACK FOOTER //
// ./src/circles/circle.js